import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Car, CreditCard, FileText, Star } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat';
import {
  fetchConversations,
  fetchDocumentRequests,
  fetchLessons,
  fetchPayableInvoices,
  fetchSlots,
  MESSAGING_REFRESH_MS,
} from '../api/candidate';
import {
  disablePush,
  enablePush,
  getPushState,
  PushState,
} from '../api/push';
import {
  getWebauthnStatus,
  registerPasskey,
  webauthnSupported,
} from '../api/webauthn';
import { initials } from '../lib/dates';
import { useMyPhotos } from './Photos';
import { slotLabel } from '../lib/format';
import { awaitsStudent } from '../types';
import DashboardView from '../views/DashboardView';
import ReserveView from '../views/ReserveView';
import CommandView from '../views/CommandView';
import PaymentsView from '../views/PaymentsView';
import DocumentsView from '../views/DocumentsView';
import LivretView from '../views/LivretView';
import EvaluationView from '../views/EvaluationView';
import PermisView from '../views/PermisView';
import MessagesView from '../views/MessagesView';
import SupportView from '../views/SupportView';
import InstallAppBanner from './InstallAppBanner';
import { fetchLicenceFollowup, fetchPendingRatings, fetchStartingEvaluation } from '../api/candidate';
import { ratingPromptKey } from '../lib/instructorRating';
import { InstructorRatingSheet, PENDING_RATINGS_KEY } from './InstructorRatingSheet';
import { reminderSnoozeKey, reminderSnoozed } from '../lib/permis';
import { NAV, TAB_IDS, type Tab } from './shell/nav';
import { BottomBar, TopTabs } from './shell/Navigation';
import { AccountMenu, NotificationsMenu, type Notice } from './shell/HeaderMenus';
import { PasswordSheet, ProfileSheet } from './shell/AccountSheets';
import { EvaluationReminder, LicenceReminder, RatingReminder } from './shell/Reminders';

/** Report du rappel « Félicitations pour votre permis ! » lu dans le navigateur (aucun s'il est indisponible). */
function readReminderSnooze(key: string | null): string | null {
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export default function Layout() {
  const candidate = useAuthStore((s) => s.candidate);
  const setCandidate = useAuthStore((s) => s.setCandidate);
  const logout = useAuthStore((s) => s.logout);
  usePresenceHeartbeat(!!candidate);
  const [tab, setTab] = useState<Tab>(() => {
    const seg = window.location.pathname.replace(/^\/+/, '');
    return TAB_IDS.includes(seg as Tab) ? (seg as Tab) : 'dashboard';
  });

  // Évaluation de départ : l'onglet n'apparaît que si l'auto-école en a demandé
  // une (à réaliser ou déjà réalisée).
  const evaluationQuery = useQuery({
    queryKey: ['candidate', 'starting-evaluation'],
    queryFn: fetchStartingEvaluation,
    staleTime: 60 * 1000,
  });
  const evalStatus = evaluationQuery.data?.status ?? 'not_requested';
  // Mon permis : l'onglet n'apparaît qu'une fois le parcours « permis obtenu » lancé par
  // l'auto-école ; une erreur de lecture le laisse caché.
  const licenceQuery = useQuery({
    queryKey: ['candidate', 'licence-followup'],
    queryFn: fetchLicenceFollowup,
    staleTime: 60 * 1000,
  });
  const licenceFollowup = licenceQuery.data ?? null;
  const evalPending = evalStatus === 'pending';
  // « Mes paiements » masqué si l'auto-école ne l'autorise pas (Accès Candidat).
  const canViewPayments = candidate?.access?.canViewPayments !== false;
  const TABS = NAV.filter(
    (t) =>
      (t.id !== 'paiements' || canViewPayments) &&
      (t.id !== 'evaluation' || evalStatus !== 'not_requested') &&
      (t.id !== 'permis' || !!licenceFollowup),
  );
  // Onglets dans la barre du haut à partir de 1024 px ; avec plus de 7 onglets (évaluation, permis),
  // à partir de 1280 px seulement — en dessous, barre du bas et « Plus ».
  const topTabs = TABS.filter((t) => t.id !== 'support');
  const wideNav = topTabs.length > 7;
  // Rappel du permis reporté (« Plus tard ») : mémorisé par élève dans le navigateur, il revient
  // au bout de 7 jours (lib/permis.ts).
  const snoozeKey = candidate ? reminderSnoozeKey(candidate.id) : null;
  const [reminderSnoozedAt, setReminderSnoozedAt] = useState<string | null>(() => readReminderSnooze(snoozeKey));
  useEffect(() => {
    setReminderSnoozedAt(readReminderSnooze(snoozeKey));
  }, [snoozeKey]);
  const snoozeLicenceReminder = () => {
    const at = new Date().toISOString();
    setReminderSnoozedAt(at);
    try {
      if (snoozeKey) localStorage.setItem(snoozeKey, at);
    } catch {
      // stockage indisponible : masqué pour la session seulement.
    }
  };
  const showLicenceReminder =
    !!licenceFollowup && !licenceFollowup.survey.answeredAt && tab !== 'permis' && !reminderSnoozed(reminderSnoozedAt, new Date());
  // Adresse /permis sans parcours (lien ancien, parcours absent) : retour au tableau de bord.
  useEffect(() => {
    if (tab === 'permis' && !licenceQuery.isPending && !licenceFollowup) setTab('dashboard');
  }, [tab, licenceQuery.isPending, licenceFollowup]);
  const [profileOpen, setProfileOpen] = useState(false);

  // Notation du moniteur après chaque leçon émargée : la fenêtre s'ouvre d'elle-même une fois par
  // session (la plus récente leçon à noter), puis un bandeau la rappelle tant qu'il en reste.
  const ratingsQuery = useQuery({
    queryKey: PENDING_RATINGS_KEY,
    queryFn: fetchPendingRatings,
    staleTime: 60 * 1000,
  });
  const lessonsToRate = ratingsQuery.data?.lessons ?? [];
  const lessonToRate = lessonsToRate[0] ?? null;
  const [ratingOpen, setRatingOpen] = useState(false);
  useEffect(() => {
    if (!lessonToRate || !candidate) return;
    const key = ratingPromptKey(candidate.id);
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // stockage indisponible : la fenêtre s'ouvre à chaque chargement.
    }
    setRatingOpen(true);
  }, [lessonToRate, candidate]);

  const lastPage = useRef('/');
  // Reflète l'onglet courant dans l'URL (évite de rester bloqué sur /login).
  useEffect(() => {
    const path = tab === 'dashboard' ? '/' : `/${tab}`;
    if (window.location.pathname !== path) {
      window.history.replaceState({}, '', path);
    }
    // Page d'où part un signalement de problème (jointe au message, comme avant depuis la messagerie).
    if (tab !== 'support') lastPage.current = path;
  }, [tab]);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pushState, setPushState] = useState<PushState>('default');
  const [pushBusy, setPushBusy] = useState(false);
  // La permission navigateur est-elle déjà accordée ? (évite de re-proposer
  // « Activer » quand l'abonnement local n'a pas encore été rétabli.)
  const notifAlreadyGranted =
    typeof Notification !== 'undefined' && Notification.permission === 'granted';
  const [readNotifIds, setReadNotifIds] = useState<string[]>([]);

  // Notifications lues : mémorisées par candidat dans le navigateur.
  const readKey = candidate ? `pmd-candidat-notif-read:${candidate.id}` : null;
  useEffect(() => {
    if (!readKey) return;
    try {
      const raw = localStorage.getItem(readKey);
      setReadNotifIds(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setReadNotifIds([]);
    }
  }, [readKey]);
  useEffect(() => {
    if (!readKey) return;
    try {
      localStorage.setItem(readKey, JSON.stringify(readNotifIds));
    } catch {
      // stockage indisponible : ignoré.
    }
  }, [readKey, readNotifIds]);

  useEffect(() => {
    getPushState()
      .then(async (st) => {
        // Permission déjà accordée mais abonnement local manquant (SW réinstallé,
        // etc.) : on ré-abonne silencieusement pour ne pas re-demander l'activation.
        if (
          st === 'default' &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          try {
            setPushState(await enablePush());
            return;
          } catch {
            // échec du ré-abonnement : on garde l'état courant.
          }
        }
        setPushState(st);
      })
      .catch(() => undefined);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      setPushState(
        pushState === 'subscribed' ? await disablePush() : await enablePush(),
      );
    } catch {
      // silencieux : l'utilisateur peut réessayer.
    } finally {
      setPushBusy(false);
    }
  };

  const [bioRegistered, setBioRegistered] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const bioSupported = webauthnSupported();

  useEffect(() => {
    if (!bioSupported) return;
    getWebauthnStatus()
      .then(setBioRegistered)
      .catch(() => undefined);
  }, [bioSupported]);

  const enableBiometric = async () => {
    setBioBusy(true);
    try {
      await registerPasskey();
      setBioRegistered(true);
    } catch {
      // annulé ou non supporté : on ne change rien.
    } finally {
      setBioBusy(false);
    }
  };
  // Notifications dérivées des vraies données (prochaine leçon, créneaux ouverts).
  const lessonsQuery = useQuery({
    queryKey: ['candidate', 'lessons'],
    queryFn: fetchLessons,
  });
  const slotsQuery = useQuery({
    queryKey: ['candidate', 'slots'],
    queryFn: fetchSlots,
  });
  const docRequestsQuery = useQuery({
    queryKey: ['candidate', 'document-requests'],
    queryFn: fetchDocumentRequests,
  });
  const payableQuery = useQuery({
    queryKey: ['candidate', 'payable-invoices'],
    queryFn: fetchPayableInvoices,
  });
  // Messages non lus : compteur de l'onglet « Messages », relu périodiquement.
  const conversationsQuery = useQuery({
    queryKey: ['candidate', 'messaging', 'conversations'],
    queryFn: fetchConversations,
    refetchInterval: MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const unreadMessages = (conversationsQuery.data ?? []).reduce(
    (total, c) => total + c.unread,
    0,
  );
  // Première leçon non terminée (la liste inclut aussi les leçons passées).
  const nextLesson =
    (lessonsQuery.data ?? []).find(
      (l) => new Date(l.endsAt).getTime() >= Date.now(),
    ) ?? null;
  const openSlots = slotsQuery.data?.length ?? 0;
  const pendingDocs = (docRequestsQuery.data ?? []).filter(awaitsStudent).length;
  const payableInvoices = payableQuery.data?.paymentEnabled
    ? (payableQuery.data?.invoices.length ?? 0)
    : 0;
  const notifications: Notice[] = [];
  if (payableInvoices > 0) {
    notifications.push({
      id: `invoices:${payableInvoices}`,
      icon: <CreditCard size={14} className="text-primary" />,
      title: `${payableInvoices} facture${payableInvoices > 1 ? 's' : ''} à régler`,
      desc: 'Réglez vos factures en ligne depuis « Mes paiements ».',
      tab: 'paiements',
    });
  }
  if (pendingDocs > 0) {
    notifications.push({
      id: `docs:${pendingDocs}`,
      icon: <FileText size={14} className="text-amber-600" />,
      title: `${pendingDocs} document${pendingDocs > 1 ? 's' : ''} à fournir`,
      desc: 'Votre auto-école vous réclame des pièces. Rendez-vous dans « Mes documents ».',
      tab: 'documents',
    });
  }
  if (lessonToRate) {
    notifications.push({
      id: `rating:${lessonToRate.lessonId}`,
      icon: <Star size={14} className="text-amber-500" />,
      title: 'Notez votre dernière leçon',
      desc: `Votre avis sur ${lessonToRate.instructorName || 'votre moniteur'} en quelques secondes.`,
      tab: 'dashboard',
    });
  }
  if (nextLesson) {
    notifications.push({
      id: `lesson:${nextLesson.startsAt}`,
      icon: <Car size={14} className="text-primary" />,
      title: 'Rappel : votre prochaine leçon',
      desc: slotLabel(nextLesson.startsAt, nextLesson.endsAt),
      tab: 'dashboard',
    });
  }
  if (openSlots > 0) {
    notifications.push({
      id: `slots:${openSlots}`,
      icon: <Calendar size={14} className="text-success" />,
      title: `${openSlots} créneau${openSlots > 1 ? 'x' : ''} disponible${
        openSlots > 1 ? 's' : ''
      }`,
      desc: 'Réservez votre prochaine leçon en un clic.',
      tab: 'reserver',
    });
  }

  const markAllRead = () => {
    const ids = notifications.map((n) => n.id);
    setReadNotifIds((prev) => Array.from(new Set([...prev, ...ids])));
  };
  const markRead = (notifId: string) =>
    setReadNotifIds((prev) => (prev.includes(notifId) ? prev : [...prev, notifId]));

  const fullName = candidate
    ? `${candidate.firstName} ${candidate.lastName}`
    : '';
  const monogram = candidate
    ? initials(candidate.firstName, candidate.lastName)
    : '··';
  const myPhotos = useMyPhotos();

  return (
    <div className="min-h-screen bg-surface-light" data-nav={wideNav ? 'wide' : 'narrow'}>
      {/* ── En-tête ────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-line bg-white/90 backdrop-blur safe-top">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
          <img src="/logo-permio-eleve.png" alt="Permio" className="h-7 w-auto flex-shrink-0" />
          <TopTabs items={topTabs} tab={tab} onTab={setTab} unreadMessages={unreadMessages} wide={wideNav} />
          <div className="flex flex-shrink-0 items-center gap-1">
            <NotificationsMenu
              notifications={notifications}
              readIds={readNotifIds}
              onRead={markRead}
              onReadAll={markAllRead}
              onTab={setTab}
              pushState={pushState}
              pushBusy={pushBusy}
              pushGranted={notifAlreadyGranted}
              onTogglePush={togglePush}
            />
            <AccountMenu
              fullName={fullName}
              email={candidate?.email}
              monogram={monogram}
              photo={myPhotos.me}
              pushState={pushState}
              pushBusy={pushBusy}
              onTogglePush={togglePush}
              bioSupported={bioSupported}
              bioRegistered={bioRegistered}
              bioBusy={bioBusy}
              onEnableBio={enableBiometric}
              hasPassword={!!candidate?.hasPassword}
              onProfile={() => setProfileOpen(true)}
              onPassword={() => setPwdOpen(true)}
              onSupport={() => setTab('support')}
              onLogout={() => logout()}
            />
          </div>
        </div>
      </header>

      <InstallAppBanner />

      {/* Rappels : évaluation de départ à réaliser ; permis obtenu (« Plus tard » le reporte de 7 jours) */}
      {((evalPending && tab !== 'evaluation') || showLicenceReminder || (!!lessonToRate && !ratingOpen)) && (
        <div className="mx-auto grid max-w-6xl gap-3 px-4 pt-5 sm:px-6 sm:pt-6">
          {lessonToRate && !ratingOpen && (
            <RatingReminder
              instructorName={lessonToRate.instructorName}
              count={lessonsToRate.length}
              onOpen={() => setRatingOpen(true)}
            />
          )}
          {evalPending && tab !== 'evaluation' && <EvaluationReminder onOpen={() => setTab('evaluation')} />}
          {showLicenceReminder && <LicenceReminder onOpen={() => setTab('permis')} onSnooze={snoozeLicenceReminder} />}
        </div>
      )}

      {/* ── Contenu (marge basse : place de la barre de navigation du téléphone) ── */}
      <main className={`mx-auto max-w-6xl px-4 py-6 pb-28 sm:px-6 sm:py-8 sm:pb-28 ${wideNav ? 'xl:pb-8' : 'lg:pb-8'}`}>
        {tab === 'dashboard' && (
          <DashboardView onReserve={() => setTab('reserver')} />
        )}
        {tab === 'reserver' && <ReserveView />}
        {tab === 'commander' && <CommandView />}
        {tab === 'paiements' && <PaymentsView />}
        {tab === 'documents' && <DocumentsView />}
        {tab === 'livret' && <LivretView />}
        {tab === 'messages' && <MessagesView />}
        {tab === 'evaluation' && <EvaluationView onOpenLivret={() => setTab('livret')} />}
        {tab === 'support' && <SupportView fromPath={lastPage.current} onDone={() => setTab('dashboard')} />}
        {tab === 'permis' && licenceFollowup && (
          <PermisView followup={licenceFollowup} onOpenDocuments={() => setTab('documents')} />
        )}
      </main>

      <BottomBar items={TABS} tab={tab} onTab={setTab} unreadMessages={unreadMessages} wide={wideNav} />

      {pwdOpen && (
        <PasswordSheet
          hasPassword={!!candidate?.hasPassword}
          onClose={() => setPwdOpen(false)}
          onSaved={() => {
            if (candidate) setCandidate({ ...candidate, hasPassword: true });
            setPwdOpen(false);
          }}
        />
      )}

      {ratingOpen && lessonToRate && (
        <InstructorRatingSheet
          key={lessonToRate.lessonId}
          lesson={lessonToRate}
          criteria={ratingsQuery.data?.criteria ?? []}
          remaining={lessonsToRate.length - 1}
          onClose={() => setRatingOpen(false)}
        />
      )}

      {profileOpen && candidate && (
        <ProfileSheet candidate={candidate} onClose={() => setProfileOpen(false)} />
      )}
    </div>
  );
}
